<script lang="ts">
	import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
	import type { Snippet } from 'svelte';

	import SliderIndicator from '$lib/components/SliderIndicator.svelte';
	import emblaCarouselSvelte, { type EmblaCarouselSvelteType } from 'embla-carousel-svelte';

	import Product from './Product.svelte';

	const {
		children,
		capitalizeTitle = true,
		products,
	}: {
		children: Snippet;
		capitalizeTitle?: boolean;
		products: ProductFromCategory[];
	} = $props();

	let emblaApi: EmblaCarouselType | undefined = $state();
	let snapCount = $state(0);
	let currentSnapIndex = $state(0);
	let canScrollLeft = $state(false);
	let canScrollRight = $state(false);

	const siblingsNode = $derived(document.querySelectorAll('.siblings'));

	const options: EmblaOptionsType = {
		loop: false,
		slidesToScroll: 2,
		breakpoints: {
			'@container (width >= 32rem)': {
				slidesToScroll: 3,
			},
			'@container (width >= 48rem)': {
				slidesToScroll: 4,
			},
		},
		watchDrag: (_, event) => {
			return (
				!siblingsNode?.length ||
				!Array.from(siblingsNode).some((node) => node.contains(event.target as Node))
			);
		},
	};

	function refreshEmblaStates(emblaApi: EmblaCarouselType) {
		canScrollLeft = emblaApi.canScrollPrev();
		canScrollRight = emblaApi.canScrollNext();
		snapCount = emblaApi.scrollSnapList().length;
		currentSnapIndex = emblaApi.selectedScrollSnap();
	}

	function onInit(e: CustomEvent<EmblaCarouselSvelteType>) {
		emblaApi = e.detail as EmblaCarouselType;

		refreshEmblaStates(emblaApi);

		emblaApi.on('scroll', refreshEmblaStates);

		emblaApi.on('reInit', refreshEmblaStates);
	}
</script>

<div class="full-container @container flex flex-col gap-2">
	<div class="relative">
		<h2
			class={`text-center text-2xl font-semibold text-neutral-900 ${capitalizeTitle ? 'uppercase' : ''}`}
		>
			{@render children()}
		</h2>

		{#if canScrollLeft || canScrollRight}
			<div class="absolute top-0 right-0 hidden h-full lg:block">
				<button onclick={() => emblaApi?.scrollPrev()} aria-label="previous">
					<span
						class={[
							'icon-[mdi--chevron-left] text-2xl',
							{ 'cursor-not-allowed opacity-50': !canScrollLeft },
							{ 'cursor-pointer opacity-100': canScrollLeft },
						]}
					></span>
				</button>
				<button onclick={() => emblaApi?.scrollNext()} aria-label="next">
					<span
						class={[
							'icon-[mdi--chevron-right] text-2xl',
							{ 'cursor-not-allowed opacity-50': !canScrollRight },
							{ 'cursor-pointer opacity-100': canScrollRight },
						]}
					></span>
				</button>
			</div>
		{/if}
	</div>

	<div
		class="overflow-hidden"
		use:emblaCarouselSvelte={{ options, plugins: [] }}
		onemblaInit={onInit}
	>
		<div class="-mx-2 flex">
			{#each products as product (product.slug)}
				<div class="min-w-0 shrink-0 grow-0 basis-1/2 px-2 @sm:basis-1/3 @3xl:basis-1/4">
					<Product {product} imgLoading="eager" />
				</div>
			{/each}
		</div>
	</div>

	<SliderIndicator {emblaApi} {currentSnapIndex} {snapCount} />
</div>
